import { memo, useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { SearchQueryRequestedPage } from "types/generated";
import useFiltersVariables from "components/Filters/useFiltersVariables";

import {
  FILTERS_TYPE,
  GROUPS_LIMIT,
  GROUPS_LIMIT_STORAGE_KEY,
  initialSortDirection,
  initialSortOption,
  PAGINATION_OPTIONS,
  RESOURCES_ROWS_PER_PAGE,
} from "./constants";
import useIacTableStore from "../components/Table/useIacTableStore";
import IacManagementTable from "../components/Table";
import useIacManagementStore from "../useIacManagementStore";
import IacManagementEmpty from "../components/Empty";
import useIacManagementGroups from "../useIacManagementGroups";
import IacManagementTableGroupRow from "./TableGroupRow";
import IacManagementTableResourceRow from "./TableResourceRow";
import useIacManagementAnalytics from "../useIacManagementAnalytics";
import { IacManagementGroupBy } from "../types";
import useIacManagementResources from "../useIacManagementResources";

const IacManagementListLayout = () => {
  const { analyticPage } = useIacManagementAnalytics();

  const { setGroups, flushTable, loadResources, openGroup } = useIacTableStore(
    useShallow((state) => ({
      setGroups: state.setGroups,
      flushTable: state.flushTable,
      loadResources: state.loadResources,
      openGroup: state.openGroup,
    }))
  );

  const { allGroupsQty, setPageIsEmpty, groupBy, parentStackId } = useIacManagementStore(
    useShallow((state) => ({
      allGroupsQty: state.allItemsQty,
      setPageIsEmpty: state.setPageIsEmpty,
      groupBy: state.groupBy,
      parentStackId: state.parentStackId,
    }))
  );

  const { groups, loading, fetchData, endCursor, startCursor, isEmpty, searchIsEmpty } =
    useIacManagementGroups({ groupBy, parentStackId });
  const { fetchData: fetchResources } = useIacManagementResources();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    resetToInitialPage,
  } = usePagination({
    fetchItems: fetchData,
    endCursor,
    startCursor,
    allItemsCount: allGroupsQty,
    visibleItemsCount: groups.length,
    initialLimit: GROUPS_LIMIT,
    storageKey: GROUPS_LIMIT_STORAGE_KEY,
    analyticsPage: analyticPage,
  });

  const { filterVariables } = useFiltersVariables({
    filterType: FILTERS_TYPE,
    initialSortDirection,
    initialSortOption,
  });

  const showPagination = groupBy !== IacManagementGroupBy.None && !isEmpty;

  useEffect(() => {
    setPageIsEmpty(isEmpty);
  }, [isEmpty, setPageIsEmpty]);

  // When filters & groupBy change, we need to reset the table and fetch fresh data
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    flushTable();
    resetToInitialPage();
  }, [flushTable, resetToInitialPage, filterVariables, groupBy]);

  // Fetch initial data or reset data when filters change
  useEffect(() => {
    fetchData({
      cursor: null,
      requestedPage: SearchQueryRequestedPage.First,
      rowsPerPage,
    });
  }, [fetchData, filterVariables, rowsPerPage]);

  // Update groups when data changes. filterVariables - to force update when filters change
  useEffect(() => {
    setGroups(groups);
  }, [setGroups, groups, filterVariables]);

  /**
   * Stack views: automatically open the Stack group and load resources
   * * FYI:`filterVariables` is used to force the effect to run when filters change on Stack view
   */
  useEffect(() => {
    if (parentStackId && groupBy === IacManagementGroupBy.None) {
      openGroup(parentStackId);
      loadResources(
        {
          // it's okay to use parentStackId for both arguments, the fetchData function will handle it
          groupId: parentStackId,
          parentStackId,
          groupBy,
          rowsPerPage: RESOURCES_ROWS_PER_PAGE,
        },
        fetchResources
      );
    }
  }, [filterVariables, fetchResources, groupBy, loadResources, parentStackId, openGroup]);

  return (
    <>
      {isEmpty && <IacManagementEmpty />}
      {searchIsEmpty && <IacManagementEmpty hasNoSearchResult />}
      {!searchIsEmpty && !isEmpty && (
        <IacManagementTable
          groupsLoading={loading}
          rowsPerPage={rowsPerPage}
          renderGroupRow={IacManagementTableGroupRow}
          renderResourceRow={IacManagementTableResourceRow}
          hasPagination={showPagination}
        />
      )}

      {showPagination && (
        <TablePagination
          from={from}
          to={to}
          loading={loading}
          goToNextPage={goToNextPage}
          setRowsPerPage={setRowsPerPage}
          goToPrevPage={goToPrevPage}
          goToFirstPage={goToFirstPage}
          goToLastPage={goToLastPage}
          rowsPerPage={rowsPerPage}
          itemsCount={allGroupsQty}
          lastPage={lastPage}
          page={page}
          paginationOptions={PAGINATION_OPTIONS}
        />
      )}
    </>
  );
};

export default memo(IacManagementListLayout);
